/**
 * Promise-based replacements for native confirm()/prompt().
 *
 *   import { confirmDialog, promptDialog } from '@/composables/useDialogs';
 *
 *   if (!(await confirmDialog({ title: 'Delete batch?', message: '…', danger: true }))) return;
 *   const reason = await promptDialog({ title: 'Cancel batch', label: 'Reason' });
 *   if (reason === null) return; // user cancelled (mirrors prompt() semantics)
 *
 * Rendered by <DialogHost/> (mounted once in App.vue) on BaseModal — themed,
 * Escape/backdrop-aware, focus-trapped. Native confirm/alert/prompt are
 * unthemeable and silently no-op when browsers suppress dialogs; these don't.
 */
import { reactive } from 'vue';

export interface ConfirmOptions {
  title: string;
  message: string;
  /** Red confirm button + "cannot be undone" tone for destructive actions. */
  danger?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Optional bootstrap-icon class for the confirm button. */
  confirmIcon?: string;
}

export interface PromptOptions {
  title: string;
  message?: string;
  label?: string;
  placeholder?: string;
  /** Pre-filled value. */
  initial?: string;
  /** Use a textarea instead of a single-line input. */
  multiline?: boolean;
  confirmLabel?: string;
  /** When true an empty value keeps the OK button disabled. */
  required?: boolean;
}

interface DialogState {
  confirm: (ConfirmOptions & { resolve: (ok: boolean) => void }) | null;
  prompt: (PromptOptions & { resolve: (value: string | null) => void }) | null;
}

/** Singleton state consumed by DialogHost. */
export const dialogState = reactive<DialogState>({ confirm: null, prompt: null });

/** Themed confirm(). Resolves true when the user confirms. */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    // Only one at a time — resolve any dangling dialog as cancelled first.
    dialogState.confirm?.resolve(false);
    dialogState.confirm = { ...options, resolve };
  });
}

/** Themed prompt(). Resolves the entered string, or null when cancelled. */
export function promptDialog(options: PromptOptions): Promise<string | null> {
  return new Promise((resolve) => {
    dialogState.prompt?.resolve(null);
    dialogState.prompt = { ...options, resolve };
  });
}
